import axios from 'axios';
import apiClient from '../plugins/axios';

/** Detect upload payloads for multipart (File, Blob, Vue/observers; cross-realm safe). */
function isUploadFileValue(v) {
  if (v == null || typeof v !== 'object') return false;
  if (typeof File !== 'undefined' && v instanceof File) return true;
  if (Object.prototype.toString.call(v) === '[object File]') return true;
  if (typeof Blob !== 'undefined' && v instanceof Blob) return true;
  return false;
}

function collectMultipartParts(rawParams) {
  const entries = Object.entries(rawParams || {});
  const files = entries.filter(([, v]) => isUploadFileValue(v));
  const scalars = Object.fromEntries(
    entries.filter(([, v]) => !isUploadFileValue(v)),
  );
  return { files, scalars };
}

function multipartTransformRequest() {
  const base = axios.defaults.transformRequest;
  let chain = [];
  if (Array.isArray(base)) {
    chain = [...base];
  } else if (base) {
    chain = [base];
  }
  return [
    ...chain,
    (data, headers) => {
      if (typeof FormData !== 'undefined' && data instanceof FormData) {
        delete headers['Content-Type'];
        delete headers['content-type'];
      }
      return data;
    },
  ];
}

/**
 * TTS Service – GET /tts/voices uses JWT; POST /tts/synthesize uses X-Api-Key header.
 */
class TtsService {
  /**
   * List TTS voices (speakers). GET /tts/voices – Bearer token (JWT).
   */
  async getVoices() {
    const response = await apiClient.get('/tts/voices');
    return response.data;
  }

  /**
   * Synthesize speech. POST /tts/synthesize → audio/wav. Uses X-Api-Key header.
   * JSON body when params contain no File values; multipart/form-data when any param is a File
   * (scalar params as JSON in field `params`, each file appended under its param name).
   * @param {string} apiToken
   * @param {{ model_id: string, text: string, params?: Record<string, unknown> }} body
   * @returns {Promise<Blob>}
   */
  async synthesize(apiToken, body) {
    const { model_id: modelId, text, params: rawParams = {} } = body;
    const { files, scalars } = collectMultipartParts(rawParams);

    if (files.length === 0) {
      const response = await apiClient.post(
        '/tts/synthesize',
        { model_id: modelId, text, params: scalars },
        {
          responseType: 'blob',
          headers: { 'X-Api-Key': apiToken },
        },
      );
      return response.data;
    }

    const fd = new FormData();
    fd.append('model_id', String(modelId));
    fd.append('text', text);
    fd.append('params', JSON.stringify(scalars));
    files.forEach(([key, file]) => {
      const name = file && file.name ? file.name : 'upload';
      fd.append(key, file, name);
    });

    const response = await apiClient.post('/tts/synthesize', fd, {
      responseType: 'blob',
      headers: { 'X-Api-Key': apiToken },
      transformRequest: multipartTransformRequest(),
    });
    return response.data;
  }
}

export default new TtsService();
